/**
 * One "Request a Robotics Proposal" submission in full.
 *
 * Reached from the Robotics Proposal tab on Marketing. Shows every answer in the
 * form's own section order, carries the team's follow-up notes, and is where a
 * submission is accepted into the client database as a lead — after the user has
 * checked the values that will be saved.
 *
 * The answers themselves are read-only: this page is the record of what the visitor
 * sent. Anything that needs correcting is corrected in the verification step, which
 * only affects the lead being created.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import { DuplicateClientAlert } from '../components/DuplicateClientModal';
import { DuplicateClientError, type ExistingClientRef } from '../api/clients';
import { useAuth } from '../context/AuthContext';
import {
  convertRoboticsProposal,
  createRoboticsProposalNote,
  deleteRoboticsProposalNote,
  draftFromProposal,
  fetchRoboticsProposal,
  PROPOSAL_SECTIONS,
  proposalCompanyLabel,
  proposalContactName,
  updateRoboticsProposalNote,
  updateRoboticsProposalStatus,
  type ProposalField,
  type RoboticsProposalNote,
  type RoboticsProposalRow,
} from '../api/roboticsProposals';
import './Page.css';
import './TasksBoard.css';
import './Events.css';
import './Marketing.css';

const INBOX_PATH = '/marketing?tab=robotics-proposal';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

type ConvertDraft = ReturnType<typeof draftFromProposal>;

const RoboticsProposalDetail: React.FC = () => {
  const { proposalId } = useParams<{ proposalId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id ?? 0;
  const roles = Array.isArray(user?.roles) ? user!.roles : [];
  const hasMarketingTools = roles.includes('marketing') || roles.includes('admin');

  const id = Number(proposalId);

  const [proposal, setProposal] = useState<RoboticsProposalRow | null>(null);
  const [notes, setNotes] = useState<RoboticsProposalNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [showRaw, setShowRaw] = useState(false);

  const [noteDraft, setNoteDraft] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState('');

  const [convertOpen, setConvertOpen] = useState(false);
  const [convertDraft, setConvertDraft] = useState<ConvertDraft | null>(null);
  const [convertEditing, setConvertEditing] = useState(false);
  const [convertSaving, setConvertSaving] = useState(false);
  const [convertError, setConvertError] = useState('');
  const [convertDuplicate, setConvertDuplicate] = useState<ExistingClientRef | null>(null);
  const [convertDone, setConvertDone] = useState<{ name: string; clientId: number } | null>(null);

  const load = useCallback(async () => {
    if (!Number.isInteger(id) || id <= 0 || !userId) return;
    setLoading(true);
    setLoadError('');
    try {
      const result = await fetchRoboticsProposal(id, userId);
      setProposal(result.proposal);
      setNotes(result.notes);
    } catch (e) {
      setProposal(null);
      setLoadError(e instanceof Error ? e.message : 'Failed to load the proposal request.');
    } finally {
      setLoading(false);
    }
  }, [id, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const addNote = async () => {
    const text = noteDraft.trim();
    if (!proposal || !text) return;
    setNoteBusy(true);
    setNoteError('');
    try {
      const created = await createRoboticsProposalNote(proposal.id, text, userId);
      setNotes((prev) => [created, ...prev]);
      setNoteDraft('');
    } catch (e) {
      setNoteError(e instanceof Error ? e.message : 'Failed to add the note.');
    } finally {
      setNoteBusy(false);
    }
  };

  const saveNoteEdit = async () => {
    const text = editingNoteText.trim();
    if (!proposal || editingNoteId == null || !text) return;
    setNoteBusy(true);
    setNoteError('');
    try {
      const updated = await updateRoboticsProposalNote(proposal.id, editingNoteId, text, userId);
      setNotes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
      setEditingNoteId(null);
      setEditingNoteText('');
    } catch (e) {
      setNoteError(e instanceof Error ? e.message : 'Failed to update the note.');
    } finally {
      setNoteBusy(false);
    }
  };

  const removeNote = async (noteId: number) => {
    if (!proposal) return;
    setNoteBusy(true);
    setNoteError('');
    try {
      await deleteRoboticsProposalNote(proposal.id, noteId, userId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (e) {
      setNoteError(e instanceof Error ? e.message : 'Failed to delete the note.');
    } finally {
      setNoteBusy(false);
    }
  };

  const changeStatus = async (status: 'Pending' | 'Voided', reason?: string) => {
    if (!proposal) return;
    setStatusBusy(true);
    setStatusError('');
    try {
      setProposal(
        await updateRoboticsProposalStatus(proposal.id, {
          user_id: userId,
          status,
          ...(reason ? { void_reason: reason } : {}),
        })
      );
      setVoidOpen(false);
      setVoidReason('');
    } catch (e) {
      setStatusError(e instanceof Error ? e.message : 'Failed to update the status.');
    } finally {
      setStatusBusy(false);
    }
  };

  const openConvert = () => {
    if (!proposal) return;
    setConvertDraft(draftFromProposal(proposal));
    setConvertEditing(false);
    setConvertError('');
    setConvertDuplicate(null);
    setConvertOpen(true);
  };

  const setDraftField = <K extends keyof ConvertDraft>(key: K, value: ConvertDraft[K]) =>
    setConvertDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

  const confirmConvert = async () => {
    if (!proposal || !convertDraft) return;
    setConvertSaving(true);
    setConvertError('');
    setConvertDuplicate(null);
    try {
      const result = await convertRoboticsProposal(proposal.id, {
        user_id: userId,
        ...convertDraft,
      });
      setProposal(result.proposal);
      setConvertDone({ name: convertDraft.company, clientId: result.client_id });
      setConvertOpen(false);
    } catch (e) {
      if (e instanceof DuplicateClientError) setConvertDuplicate(e.existing);
      else setConvertError(e instanceof Error ? e.message : 'Failed to add lead.');
    } finally {
      setConvertSaving(false);
    }
  };

  /** Renders one answer, or null when it's a conditional follow-up that went unasked. */
  const renderField = (p: RoboticsProposalRow, field: ProposalField) => {
    const value = p[field.key];

    if (field.kind === 'bool') {
      return (
        <div key={field.key}>
          <dt>{field.label}</dt>
          <dd>{value ? 'Yes' : 'No'}</dd>
        </div>
      );
    }

    if (field.kind === 'list') {
      const items = Array.isArray(value) ? value : [];
      if (items.length === 0 && field.followUp) return null;
      return (
        <div key={field.key} className="robotics-proposal-field--wide">
          <dt>{field.label}</dt>
          <dd>
            {items.length === 0 ? (
              '—'
            ) : (
              <span className="website-lead-tag-list">
                {items.map((item) => (
                  <span key={item} className="website-lead-tag">
                    {item}
                  </span>
                ))}
              </span>
            )}
          </dd>
        </div>
      );
    }

    const text = typeof value === 'string' ? value.trim() : '';
    // Conditional questions are absent from the payload rather than empty, so a
    // blank follow-up means it was never shown. Printing a dash for it would imply
    // the visitor skipped a question they were never asked.
    if (!text && field.followUp) return null;

    let body: React.ReactNode = text || '—';
    if (text && field.key === 'email') body = <a href={`mailto:${text}`}>{text}</a>;
    if (text && field.key === 'phone') body = <a href={`tel:${text}`}>{text}</a>;

    return (
      <div
        key={field.key}
        className={field.kind === 'long' ? 'robotics-proposal-field--wide' : undefined}
      >
        <dt>{field.label}</dt>
        <dd className={field.kind === 'long' ? 'website-lead-message' : undefined}>{body}</dd>
      </div>
    );
  };

  const rawEntries = useMemo(() => {
    const raw = proposal?.raw_payload;
    return raw && typeof raw === 'object' ? Object.entries(raw) : [];
  }, [proposal]);

  const header = (
    <PageHeader
      title={proposal ? proposalCompanyLabel(proposal) : 'Robotics Proposal Request'}
      subtitle={
        proposal
          ? `Submitted ${formatDateTime(proposal.created_at)} by ${proposalContactName(proposal)}`
          : 'Robotics proposal request'
      }
      onBack={() => navigate(INBOX_PATH)}
      backLabel="Back to Robotics Proposal"
    />
  );

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <div>{header}</div>

          <div className="page-content">
            {!user ? (
              <p className="page-subtitle">Log in to view proposal requests.</p>
            ) : !hasMarketingTools ? (
              <p className="page-subtitle">You do not have access to marketing tools.</p>
            ) : loading ? (
              <p className="page-subtitle">Loading proposal request…</p>
            ) : loadError ? (
              <p className="create-order-error" role="alert">
                {loadError}
              </p>
            ) : !proposal ? (
              <p className="page-subtitle">
                This proposal request could not be found. <Link to={INBOX_PATH}>Back to the list</Link>.
              </p>
            ) : (
              <>
                {/* Triage: status, the lead it became, and the actions available. */}
                <section className="events-table-section">
                  <div className="robotics-proposal-status-bar">
                    <span
                      className={`robotics-proposal-status robotics-proposal-status--${proposal.status.toLowerCase()}`}
                    >
                      {proposal.status}
                    </span>
                    {proposal.status_changed_at ? (
                      <span className="website-lead-muted">
                        {proposal.status_changed_by
                          ? `by ${proposal.status_changed_by} · `
                          : ''}
                        {formatDateTime(proposal.status_changed_at)}
                      </span>
                    ) : null}
                    <div className="robotics-proposal-status-actions">
                      {proposal.converted_client_id != null ? (
                        <Link
                          to={`/lead/${proposal.converted_client_id}`}
                          className="website-lead-add-btn"
                        >
                          Open the lead
                        </Link>
                      ) : (
                        <button
                          type="button"
                          className="website-lead-add-btn"
                          onClick={openConvert}
                          disabled={statusBusy}
                        >
                          Convert to lead
                        </button>
                      )}
                      {proposal.status === 'Voided' ? (
                        <button
                          type="button"
                          className="website-lead-status-btn"
                          disabled={statusBusy}
                          onClick={() => void changeStatus('Pending')}
                        >
                          {statusBusy ? 'Working…' : 'Unvoid'}
                        </button>
                      ) : proposal.converted_client_id == null ? (
                        <button
                          type="button"
                          className="website-lead-status-btn"
                          disabled={statusBusy}
                          onClick={() => {
                            setVoidReason('');
                            setStatusError('');
                            setVoidOpen(true);
                          }}
                        >
                          Void
                        </button>
                      ) : null}
                    </div>
                  </div>

                  {convertDone ? (
                    <p className="marketing-order-notice" role="status">
                      {convertDone.name} was added to the client database and the sales team has
                      been notified. <Link to={`/lead/${convertDone.clientId}`}>Open the lead</Link>.
                    </p>
                  ) : null}
                  {proposal.converted_client_id != null && !convertDone ? (
                    <p className="website-lead-muted">
                      Added to the client database as{' '}
                      <Link to={`/lead/${proposal.converted_client_id}`}>
                        {proposal.converted_client_company || 'this lead'}
                      </Link>
                      .
                    </p>
                  ) : null}
                  {proposal.status === 'Voided' && proposal.void_reason ? (
                    <p className="website-lead-muted">Voided because: {proposal.void_reason}</p>
                  ) : null}
                  {statusError ? (
                    <p className="create-order-error" role="alert">
                      {statusError}
                    </p>
                  ) : null}
                </section>

                {/* Every answer, in the order the visitor saw the questions. */}
                {PROPOSAL_SECTIONS.map((section) => {
                  const rendered = section.fields
                    .map((field) => renderField(proposal, field))
                    .filter(Boolean);
                  if (rendered.length === 0) return null;
                  return (
                    <section className="events-table-section" key={section.title}>
                      <h2 className="events-section-title">{section.title}</h2>
                      <dl className="website-lead-details robotics-proposal-details">{rendered}</dl>
                    </section>
                  );
                })}

                {/* Notes: who said what, when. */}
                <section className="events-table-section" aria-labelledby="proposal-notes-heading">
                  <h2 className="events-section-title" id="proposal-notes-heading">
                    Notes <span className="events-section-count">({notes.length})</span>
                  </h2>
                  {noteError ? (
                    <p className="create-order-error" role="alert">
                      {noteError}
                    </p>
                  ) : null}
                  <div className="robotics-proposal-note-add">
                    <textarea
                      className="website-lead-notes"
                      rows={3}
                      value={noteDraft}
                      placeholder="Add a note for the team…"
                      onChange={(e) => setNoteDraft(e.target.value)}
                    />
                    <button
                      type="button"
                      className="update-button"
                      disabled={noteBusy || !noteDraft.trim()}
                      onClick={() => void addNote()}
                    >
                      {noteBusy ? 'Saving…' : 'Add note'}
                    </button>
                  </div>

                  {notes.length === 0 ? (
                    <p className="page-subtitle">No notes yet.</p>
                  ) : (
                    <ul className="robotics-proposal-notes">
                      {notes.map((note) => (
                        <li key={note.id} className="robotics-proposal-note">
                          <div className="robotics-proposal-note-meta">
                            <strong>{note.submitted_by ?? 'Unknown'}</strong>
                            <span className="website-lead-muted">
                              {formatDateTime(note.created_at)}
                              {note.updated_at && note.updated_at !== note.created_at
                                ? ' · edited'
                                : ''}
                            </span>
                          </div>
                          {editingNoteId === note.id ? (
                            <>
                              <textarea
                                className="website-lead-notes"
                                rows={3}
                                value={editingNoteText}
                                onChange={(e) => setEditingNoteText(e.target.value)}
                              />
                              <div className="robotics-proposal-note-actions">
                                <button
                                  type="button"
                                  className="update-button"
                                  disabled={noteBusy || !editingNoteText.trim()}
                                  onClick={() => void saveNoteEdit()}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="cancel-button"
                                  disabled={noteBusy}
                                  onClick={() => {
                                    setEditingNoteId(null);
                                    setEditingNoteText('');
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <p className="website-lead-message">{note.note}</p>
                              {/* Only the author can change a note, matching client notes. */}
                              {note.user_id === userId ? (
                                <div className="robotics-proposal-note-actions">
                                  <button
                                    type="button"
                                    className="website-lead-status-btn"
                                    disabled={noteBusy}
                                    onClick={() => {
                                      setEditingNoteId(note.id);
                                      setEditingNoteText(note.note);
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="website-lead-status-btn"
                                    disabled={noteBusy}
                                    onClick={() => void removeNote(note.id)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              ) : null}
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                {/* The untouched body. Worth keeping reachable: when the website adds a
                    question before the portal has a column for it, this is where the
                    answer shows up. */}
                <section className="events-table-section">
                  <h2 className="events-section-title">
                    <button
                      type="button"
                      className="robotics-proposal-raw-toggle"
                      aria-expanded={showRaw}
                      onClick={() => setShowRaw((v) => !v)}
                    >
                      Raw submission ({rawEntries.length}){' '}
                      <span className={`collapse-arrow${showRaw ? ' collapse-arrow--open' : ''}`}>
                        ▶
                      </span>
                    </button>
                  </h2>
                  {showRaw ? (
                    <>
                      <p className="page-subtitle">
                        Exactly what the website sent, field name for field name.
                      </p>
                      {rawEntries.length === 0 ? (
                        <p className="page-subtitle">Nothing recorded.</p>
                      ) : (
                        <dl className="website-lead-details robotics-proposal-details">
                          {rawEntries.map(([key, value]) => (
                            <div key={key}>
                              <dt>{key}</dt>
                              <dd>{value || '—'}</dd>
                            </div>
                          ))}
                        </dl>
                      )}
                      <p className="website-lead-muted">
                        Received from {proposal.submitted_ip || 'an unknown address'}.
                      </p>
                    </>
                  ) : null}
                </section>
              </>
            )}
          </div>
        </main>
      </div>

      {/* Voiding needs a reason, so a rejected submission explains itself later. */}
      <Modal isOpen={voidOpen} onClose={() => (statusBusy ? undefined : setVoidOpen(false))} title="Void this submission">
        <div className="modal-body">
          <p className="page-subtitle">
            Voided submissions stay on record and can be unvoided. Say why, so the next person
            reading this knows.
          </p>
          {statusError ? (
            <p className="create-order-error" role="alert">
              {statusError}
            </p>
          ) : null}
          <label className="marketing-order-field">
            <span>Reason *</span>
            <textarea
              rows={4}
              value={voidReason}
              placeholder="Spam, duplicate, out of scope…"
              onChange={(e) => setVoidReason(e.target.value)}
            />
          </label>
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="cancel-button"
            disabled={statusBusy}
            onClick={() => setVoidOpen(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="update-button"
            disabled={statusBusy || !voidReason.trim()}
            onClick={() => void changeStatus('Voided', voidReason.trim())}
          >
            {statusBusy ? 'Voiding…' : 'Void submission'}
          </button>
        </div>
      </Modal>

      {/* Verification before the submission becomes a lead. Shown read-only first so
          the values get looked at rather than tabbed through. */}
      <Modal
        isOpen={convertOpen}
        onClose={() => (convertSaving ? undefined : setConvertOpen(false))}
        title="Convert to lead"
        wide
      >
        {convertDraft ? (
          <>
            <div className="modal-body">
              <p className="page-subtitle">
                {convertEditing
                  ? 'Adjust anything that needs cleaning up, then confirm.'
                  : 'Check the details below before adding this lead. Choose Edit to change anything.'}
              </p>
              {convertError ? (
                <p className="create-order-error" role="alert">
                  {convertError}
                </p>
              ) : null}
              {convertDuplicate ? <DuplicateClientAlert existing={convertDuplicate} /> : null}

              <div className="marketing-order-grid">
                <label className="marketing-order-field">
                  <span>Company *</span>
                  {convertEditing ? (
                    <input
                      type="text"
                      value={convertDraft.company}
                      onChange={(e) => setDraftField('company', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.company || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Point of contact *</span>
                  {convertEditing ? (
                    <input
                      type="text"
                      value={convertDraft.point_of_contact}
                      onChange={(e) => setDraftField('point_of_contact', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.point_of_contact || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Email</span>
                  {convertEditing ? (
                    <input
                      type="email"
                      value={convertDraft.contact_email}
                      onChange={(e) => setDraftField('contact_email', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.contact_email || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Phone</span>
                  {convertEditing ? (
                    <input
                      type="tel"
                      value={convertDraft.contact_phone}
                      onChange={(e) => setDraftField('contact_phone', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.contact_phone || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Source</span>
                  {convertEditing ? (
                    <input
                      type="text"
                      value={convertDraft.source}
                      onChange={(e) => setDraftField('source', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.source || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Industry</span>
                  {convertEditing ? (
                    <input
                      type="text"
                      value={convertDraft.industry}
                      onChange={(e) => setDraftField('industry', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.industry || '—'}</p>
                  )}
                </label>
              </div>
              <label className="marketing-order-field">
                <span>Notes</span>
                {convertEditing ? (
                  <textarea
                    rows={10}
                    value={convertDraft.notes}
                    onChange={(e) => setDraftField('notes', e.target.value)}
                  />
                ) : (
                  <p className="website-lead-message">{convertDraft.notes || '—'}</p>
                )}
              </label>
              <p className="website-lead-muted">
                Confirming marks this submission Approved and emails the sales team.
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-button"
                disabled={convertSaving}
                onClick={() => setConvertOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="website-lead-status-btn"
                disabled={convertSaving}
                onClick={() => setConvertEditing((v) => !v)}
              >
                {convertEditing ? 'Done editing' : 'Edit'}
              </button>
              <button
                type="button"
                className="update-button"
                disabled={
                  convertSaving ||
                  !convertDraft.company.trim() ||
                  !convertDraft.point_of_contact.trim()
                }
                onClick={() => void confirmConvert()}
              >
                {convertSaving ? 'Adding…' : 'Confirm & add lead'}
              </button>
            </div>
          </>
        ) : null}
      </Modal>
    </div>
  );
};

export default RoboticsProposalDetail;
